import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'
import { HelpIcon } from './HelpIcon'
import s from './Controls.module.scss'

interface FieldWrapProps {
  label?: string
  help?: ReactNode
  hint?: string
  error?: string
  children: ReactNode
  id: string
}

/**
 * Beschriftung, Feld, Hinweis — in dieser Reihenfolge.
 *
 * Die Beschriftung hängt über `htmlFor` am Feld. Das ist nicht Formsache: Ohne
 * die Verbindung setzt ein Klick auf die Beschriftung den Fokus nicht ins Feld,
 * und ein Bildschirmleser nennt das Feld unbenannt.
 */
function FieldWrap({ label, help, hint, error, children, id }: FieldWrapProps) {
  return (
    <div className={s.field}>
      {label && (
        <label className={s.label} htmlFor={id}>
          {label}
          {help && <HelpIcon term={label}>{help}</HelpIcon>}
        </label>
      )}
      {children}
      {error ? <span className={s.error}>{error}</span>
             : hint && <span className={s.hint}>{hint}</span>}
    </div>
  )
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  help?: ReactNode
  hint?: string
  error?: string
}

export function Input({ label, help, hint, error, ...rest }: InputProps) {
  const generated = useId()
  const id = rest.id ?? generated
  return (
    <FieldWrap label={label} help={help} hint={hint} error={error} id={id}>
      <input {...rest} id={id} className={s.input}
             aria-invalid={error ? true : undefined} />
    </FieldWrap>
  )
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  help?: ReactNode
  hint?: string
  children: ReactNode
}

export function Select({ label, help, hint, children, ...rest }: SelectProps) {
  const generated = useId()
  const id = rest.id ?? generated
  return (
    <FieldWrap label={label} help={help} hint={hint} id={id}>
      <select {...rest} id={id} className={s.select}>{children}</select>
    </FieldWrap>
  )
}

/**
 * Ein Schalter mit zwei Zuständen.
 *
 * `aria-pressed` trägt den Zustand, nicht eine Klasse: Damit weiß auch ein
 * Bildschirmleser, ob der Filter an ist — eine Klasse allein sieht nur das
 * Auge.
 */
export function Toggle({ on, onChange, children }: {
  on: boolean
  onChange: (on: boolean) => void
  children: ReactNode
}) {
  return (
    <button type="button" className={s.toggle} aria-pressed={on}
            onClick={() => onChange(!on)}>
      <span className={s.mark} aria-hidden="true">{on ? '✓' : ''}</span>
      {children}
    </button>
  )
}
